import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SubmissionGuard } from 'src/app/models/submission-guard.model';

@Injectable({
  providedIn: 'root'
})
export class SubmissionGuardService {
  private baseUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  getGuardsForTemplate(templateId: number): Observable<SubmissionGuard[]> {
    return this.http.get<SubmissionGuard[]>(
      `${this.baseUrl}/submissionguard/template/${templateId}`
    );
  }

  saveAcknowledgment(logSheetId: number, logSheetTemplateId: number, guardId: number): Observable<any> {
    return this.http.post(`${this.baseUrl}/guardacknowledgment`, {
      logSheetId,
      logSheetTemplateId,
      guardId
    });
  }

  createGuard(guard: Partial<SubmissionGuard>): Observable<SubmissionGuard> {
    return this.http.post<SubmissionGuard>(`${this.baseUrl}/submissionguard`, guard);
  }

  updateGuard(id: number, guard: Partial<SubmissionGuard>): Observable<SubmissionGuard> {
    return this.http.patch<SubmissionGuard>(`${this.baseUrl}/submissionguard/${id}`, guard);
  }

  deleteGuard(id: number): Observable<void> {
    return this.http.patch<void>(`${this.baseUrl}/submissionguard/deactivate/${id}`, {});
  }

  /**
   * Evaluates a guard condition against the current data point values.
   * Returns true when the guard condition IS MET (i.e. a modal should be shown).
   * Returns false when the condition is not met or the guard has no condition (always fires).
   */
  evaluateGuard(guard: SubmissionGuard, dataPointValues: { dataPointId: number; value: string }[]): boolean {
    if (guard.sourceDataPointId === null || guard.operator === null || guard.thresholdValue === null) {
      return true;
    }

    const entry = dataPointValues.find(v => v.dataPointId === guard.sourceDataPointId);
    if (!entry) return false;

    const fieldValue = entry.value ?? '';
    const threshold = guard.thresholdValue;
    const numericField = parseFloat(fieldValue);
    const numericThreshold = parseFloat(threshold);
    const bothNumeric = !isNaN(numericField) && !isNaN(numericThreshold);

    switch (guard.operator) {
      case '<':  return bothNumeric && numericField < numericThreshold;
      case '<=': return bothNumeric && numericField <= numericThreshold;
      case '>':  return bothNumeric && numericField > numericThreshold;
      case '>=': return bothNumeric && numericField >= numericThreshold;
      case '==': return bothNumeric
        ? numericField === numericThreshold
        : fieldValue.trim().toLowerCase() === threshold.trim().toLowerCase();
      case '!=': return bothNumeric
        ? numericField !== numericThreshold
        : fieldValue.trim().toLowerCase() !== threshold.trim().toLowerCase();
      case 'contains': return fieldValue.trim().toLowerCase().includes(threshold.trim().toLowerCase());
      default:   return false;
    }
  }

  /**
   * Filters and sorts guards that are triggered by the current field values.
   */
  getTriggeredGuards(
    guards: SubmissionGuard[],
    dataPointValues: { dataPointId: number; value: string }[]
  ): SubmissionGuard[] {
    return guards
      .filter(g => g.isActive && this.evaluateGuard(g, dataPointValues))
      .sort((a, b) => a.priority - b.priority);
  }

  /**
   * Replaces {value} in a warning message with the actual field value.
   */
  interpolateMessage(message: string, guard: SubmissionGuard, dataPointValues: { dataPointId: number; value: string }[]): string {
    if (guard.sourceDataPointId === null) return message;
    const entry = dataPointValues.find(v => v.dataPointId === guard.sourceDataPointId);
    return entry ? message.replace('{value}', entry.value) : message;
  }
}
